import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { WHATSAPP_CHAT_ENABLED } from "@/config/modules"
import {
  BanknotesIcon,
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ReceiptPercentIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Squares2X2Icon,
  TagIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPreview?: (entity: {
    type: string
    id: string
    path: string
    data?: any
    title?: string
    subtitle?: string
  }) => void
}

type SearchResult = {
  id: string
  type?: string
  title: string
  subtitle?: string
  path: string
  data?: any
}

type MenuItem = {
  name: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  path: string
  keywords?: string[]
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  open,
  onOpenChange,
  onPreview,
}) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [results, setResults] = useState<Record<string, SearchResult[]>>({})
  const [loadingResults, setLoadingResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Ctrl+K / Cmd+K shortcut. Comparar en minuscula: con Caps Lock o Shift el
  // navegador manda "K" mayuscula y, si no matcheaba, Brave/Chrome abrian su
  // propia busqueda. Se escucha en fase de captura para ganarle al navegador.
  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === "k") {
        event.preventDefault()
        onOpenChange(!open)
      }
    }
    document.addEventListener("keydown", down, { capture: true })
    return () => document.removeEventListener("keydown", down, { capture: true })
  }, [open, onOpenChange])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSearch("")
      setResults({})
      setSelectedIndex(0)
    } else {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounced search
  useEffect(() => {
    if (!open) return
    const query = search.trim()
    if (query.length < 2) {
      setResults({})
      return
    }
    const handle = setTimeout(async () => {
      setLoadingResults(true)
      try {
        const response = await axios.get("/api/search", {
          params: { q: query, limit: 6 },
        })
        setResults(response.data?.results || {})
        setSelectedIndex(0)
      } catch {
        setResults({})
      } finally {
        setLoadingResults(false)
      }
    }, 250)
    return () => clearTimeout(handle)
  }, [open, search])

  const menuSections = useMemo<{ heading: string; items: MenuItem[] }[]>(
    () => [
      {
        heading: "Principal",
        items: [
          { name: "Dashboard", icon: Squares2X2Icon, path: "/dashboard" },
        ],
      },
      {
        heading: "Ventas",
        items: [
          { name: "Clientes", icon: UsersIcon, path: "/clients" },
          // Chat: solo si el módulo está encendido (config/modules.ts)
          ...(WHATSAPP_CHAT_ENABLED ? [{ name: "Chat", icon: ChatBubbleLeftRightIcon, path: "/chat" }] : []),
          { name: "Caja", icon: BanknotesIcon, path: "/cash-register" },
          {
            name: "Cheques",
            icon: DocumentTextIcon,
            path: "/cash-register/checks",
          },
          {
            name: "Presupuestos",
            icon: DocumentTextIcon,
            path: "/budgets",
          },
          {
            name: "Turnos",
            icon: CalendarIcon,
            path: "/appointments-enhanced",
          },
          { name: "Remitos", icon: DocumentTextIcon, path: "/remitos" },
          { name: "Facturas", icon: DocumentTextIcon, path: "/invoices" },
          { name: "Notas de Crédito", icon: DocumentTextIcon, path: "/notas-credito" },
        ],
      },
      {
        heading: "Finanzas",
        items: [
          { name: "Ctas. Corrientes", icon: BanknotesIcon, path: "/current-accounts", keywords: ["cuentas corrientes", "cta cte", "deuda", "saldo"] },
          { name: "Cuentas", icon: BanknotesIcon, path: "/cuentas", keywords: ["tesoreria", "cuentas propias", "bancos"] },
          { name: "Posición", icon: ChartBarIcon, path: "/finanzas/posicion", keywords: ["posicion financiera", "finanzas"] },
          { name: "Gastos operativos", icon: BanknotesIcon, path: "/operating-expenses", keywords: ["egresos", "gastos"] },
          { name: "Pagos al personal", icon: UsersIcon, path: "/staff-payments", keywords: ["sueldos", "empleados", "comisiones"] },
        ],
      },
      {
        heading: "Gestion",
        items: [
          {
            name: "Gestion de Servicios",
            icon: SparklesIcon,
            path: "/active-services",
          },
          {
            name: "Productos y Servicios",
            icon: CubeIcon,
            path: "/products",
          },
          {
            name: "Compras",
            icon: ShoppingCartIcon,
            path: "/purchases",
          },
          {
            name: "Diagnosticos",
            icon: ClipboardDocumentCheckIcon,
            path: "/diagnostics",
          },
          { name: "Descuentos", icon: TagIcon, path: "/discounts" },
          { name: "Insumos", icon: CubeIcon, path: "/supplies" },
          { name: "Proveedores", icon: UsersIcon, path: "/providers" },
          { name: "Pedidos internos", icon: ClipboardDocumentCheckIcon, path: "/supply-requests" },
          { name: "Listas de precios", icon: TagIcon, path: "/price-lists" },
          { name: "Equipos y herramientas", icon: CubeIcon, path: "/assets" },
        ],
      },
      {
        heading: "Sistema",
        items: [
          {
            name: "Alertas",
            icon: ExclamationTriangleIcon,
            path: "/alerts",
          },
          { name: "Reportes", icon: ChartBarIcon, path: "/reports" },
          { name: "Reseñas", icon: SparklesIcon, path: "/reviews" },
        ],
      },
      {
        heading: "Configuración",
        items: [
          { name: "Configuración", icon: Cog6ToothIcon, path: "/settings" },
        ],
      },
    ],
    []
  )

  const resultGroups = useMemo(
    () => [
      { key: "products", label: "Articulos", icon: CubeIcon },
      { key: "clients", label: "Clientes", icon: UsersIcon },
      {
        key: "providers",
        label: "Proveedores",
        icon: ClipboardDocumentListIcon,
      },
      { key: "invoices", label: "Facturas", icon: DocumentTextIcon },
      { key: "remitos", label: "Remitos", icon: ReceiptPercentIcon },
      { key: "budgets", label: "Presupuestos", icon: DocumentTextIcon },
      { key: "purchases", label: "Compras", icon: ShoppingCartIcon },
      { key: "supplies", label: "Insumos", icon: CubeIcon },
      { key: "appointments", label: "Turnos", icon: CalendarIcon },
      {
        key: "diagnostics",
        label: "Diagnosticos",
        icon: WrenchScrewdriverIcon,
      },
    ],
    []
  )

  const hasResults = Object.values(results || {}).some(
    (items) => items && items.length > 0
  )

  // Mismo filtro para el teclado y para lo que se dibuja: si difieren, Enter abre otro ítem
  const menuMatches = (item: MenuItem, q: string) =>
    !q ||
    item.name.toLowerCase().includes(q) ||
    (item.keywords || []).some((k) => k.toLowerCase().includes(q))

  // Build flat list of all clickable items for keyboard navigation
  const allItems = useMemo(() => {
    const items: Array<{
      type: "result" | "menu"
      result?: SearchResult
      groupKey?: string
      menuItem?: MenuItem
    }> = []

    if (hasResults) {
      for (const section of resultGroups) {
        const sectionItems = results?.[section.key] || []
        for (const item of sectionItems) {
          items.push({ type: "result", result: item, groupKey: section.key })
        }
      }
    }

    const q = search.trim().toLowerCase()
    for (const section of menuSections) {
      for (const item of section.items) {
        if (menuMatches(item, q)) {
          items.push({ type: "menu", menuItem: item })
        }
      }
    }

    return items
  }, [hasResults, results, resultGroups, menuSections, search])

  // Entity types that should navigate to their full page instead of opening preview modal
  const navigateDirectTypes = new Set(["product", "supply", "client"])

  const handleResultClick = useCallback(
    (item: SearchResult) => {
      onOpenChange(false)
      if (onPreview && item.type && !navigateDirectTypes.has(item.type)) {
        setTimeout(() => {
          onPreview({
            type: item.type!,
            id: item.id,
            path: item.path,
            data: item.data,
            title: item.title,
            subtitle: item.subtitle,
          })
        }, 100)
      } else {
        setTimeout(() => navigate(item.path, { state: { product: item.data } }), 0)
      }
    },
    [onOpenChange, onPreview, navigate]
  )

  const handleMenuClick = useCallback(
    (item: MenuItem) => {
      onOpenChange(false)
      // El ítem de menú solo aparece cuando la búsqueda matchea su nombre/keyword:
      // es navegación a la página, NO un filtro. Antes se le pegaba ?q= y la página
      // filtraba por el nombre del menú (ej. "insumos") y no encontraba nada.
      setTimeout(() => navigate(item.path), 0)
    },
    [onOpenChange, navigate]
  )

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1))
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (event.key === "Enter") {
        event.preventDefault()
        const item = allItems[selectedIndex]
        if (!item) return
        if (item.type === "result" && item.result) {
          handleResultClick(item.result)
        } else if (item.type === "menu" && item.menuItem) {
          handleMenuClick(item.menuItem)
        }
      } else if (event.key === "Escape") {
        onOpenChange(false)
      }
    },
    [allItems, selectedIndex, handleResultClick, handleMenuClick, onOpenChange]
  )

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    if (el) {
      el.scrollIntoView({ block: "nearest" })
    }
  }, [selectedIndex])

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    const index = text.toLowerCase().indexOf(query.toLowerCase())
    if (index === -1) return text
    return (
      <>
        <span className="text-muted-foreground">{text.slice(0, index)}</span>
        <span className="font-semibold text-foreground">
          {text.slice(index, index + query.length)}
        </span>
        <span className="text-muted-foreground">
          {text.slice(index + query.length)}
        </span>
      </>
    )
  }

  let flatIndex = 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-[640px] gap-0">
        {/* Search input */}
        <div className="flex items-center border-b px-3">
          <MagnifyingGlassIcon className="mr-2 h-5 w-5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar clientes, productos, facturas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            autoComplete="off"
            autoFocus
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="ml-2 rounded p-1 hover:bg-muted"
            >
              <XMarkIcon className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Results list */}
        <div
          ref={listRef}
          className="max-h-[350px] overflow-y-auto overflow-x-hidden p-1"
        >
          {/* Loading/empty state */}
          {search.trim().length >= 2 && !hasResults && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {loadingResults ? "Buscando..." : "No se encontraron resultados."}
            </div>
          )}
          {search.trim().length > 0 && search.trim().length < 2 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Escribe al menos 2 caracteres para buscar.
            </div>
          )}

          {/* Search results */}
          {hasResults &&
            resultGroups.map((section) => {
              const items = results?.[section.key] || []
              if (!items.length) return null
              const SectionIcon = section.icon
              return (
                <div key={section.key} className="mb-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {section.label}
                  </div>
                  {items.map((item) => {
                    const idx = flatIndex++
                    return (
                      <button
                        key={`${section.key}-${item.id}`}
                        data-index={idx}
                        onClick={() => handleResultClick(item)}
                        className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2.5 text-sm outline-none transition-colors ${
                          selectedIndex === idx
                            ? "bg-accent text-accent-foreground"
                            : "hover:bg-accent/50"
                        }`}
                      >
                        <SectionIcon className="mr-2.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="flex flex-col text-left min-w-0">
                          <span className="font-medium truncate">
                            {highlightMatch(item.title, search)}
                          </span>
                          {item.subtitle && (
                            <span className="text-xs text-muted-foreground truncate">
                              {highlightMatch(item.subtitle, search)}
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            })}

          {/* Separator */}
          {hasResults && <div className="my-1 h-px bg-border -mx-1" />}

          {/* Menu sections */}
          {menuSections.map((section) => {
            const q = search.trim().toLowerCase()
            const filtered = section.items.filter((item) => menuMatches(item, q))
            if (!filtered.length) return null
            return (
              <div key={section.heading} className="mb-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {section.heading}
                </div>
                {filtered.map((item) => {
                  const idx = flatIndex++
                  const Icon = item.icon
                  return (
                    <button
                      key={item.path}
                      data-index={idx}
                      onClick={() => handleMenuClick(item)}
                      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors ${
                        selectedIndex === idx
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50"
                      }`}
                    >
                      <Icon className="mr-2.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium">Ir a {item.name}</span>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SearchDialog
